import { createContext, useMemo, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useTableColumns, useQuickbooksColumns } from './columns';
import { GET_INVOICE, UPDATE_INVOICE, INSERT_NOTES, SAVE_SUPPLEMENTAL_CHARGES } from './graphql';
import { INVOICE_TYPE_DISPLAY_MAPPINGS } from './constants';
import { UserContext } from '@/components/App';
import { useCanViewIntegrations } from '@/hooks';
import { post } from '@/utilities/onwardClient';
import useAction from '@/utilities/useQuery';
import { TRIGGER_ORDER_EVENT, REVERT_INVOICES_PREVIEW, REVERT_INVOICES } from '@/constants/apiRoutes';
import { INVOICE_REVERT_FIELDS } from '@/graphql/queries/carrier_invoices';
import * as ROUTES from '@/constants/routes';
import { ASSET_HEAVY } from '@/constants/featureFlags';
import { countOrdersRemaining210 } from '@/utilities/orderful';
import { calcAccessorials, calcSubtotal, calcClaims, invoiceApprovedEvent, invoiceLegRows, legRowId } from './utils';
import csvDownload from 'json-to-csv-export';
import {
    buildDelimitedText,
    generateLineItems,
    generateElectroluxRows,
    generateMasterbrandRows,
    isMasterbrandExportEnabled,
    isPelotonExportEnabled,
    downloadPelotonCsv,
} from '../Invoices/utils';
import { ELECTROLUX_COLUMNS, MASTERBRAND_COLUMNS } from '../Invoices/columns';
import JSZip from 'jszip';
import getOrdersFilesAsPDF from '@/utilities/getOrdersFilesAsPDF';
import { hasAnySubmitted210 } from '@/utilities/orderful';
import { saveAs } from 'file-saver';
import { editsDueDate } from '@/utilities/payPeriod';

const hasPOD = (order) =>
    order?.delivery_photo?.length >= 1 || order?.pod?.length >= 1 || order?.customer_signature?.length >= 1;

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { user, client, teammate } = useContext(UserContext);
    const navigate = useNavigate();
    const [viewPod, viewOrderPod] = useState(null);
    const [newComment, setNewComment] = useState(null);
    const [downloadingPODs, setDownloadingPODs] = useState(false);
    const [refreshInFlight, setRefreshInFlight] = useState(false);
    // Keyed by legRowId, so each leg of a two-leg order tracks its own re-emit.
    const [reemitInFlight, setReemitInFlight] = useState({});
    const { invoice_id } = useParams();
    const location = useLocation();
    const allInvoices = location?.state?.allInvoices || [];

    const { data, loading: initInFlight } = useQuery(GET_INVOICE, {
        variables: { invoice_id },
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
        },
    });

    const [updateInvoice, { loading: updateInFlight }] = useMutation(UPDATE_INVOICE, {
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
        },
    });

    const apolloClient = useApolloClient();
    const [revertPreview, setRevertPreview] = useState(null);
    // Bumped on every open and close, so a preview that lands after the modal closed is dropped.
    const revertPreviewRequest = useRef(0);
    const [revertNotification, setRevertNotification] = useState(null);
    const [revertError, setRevertError] = useState(null);
    useEffect(() => setRevertError(null), [invoice_id]);

    const [insertNotes, { loading: notesInFlight }] = useMutation(INSERT_NOTES, {
        onError: (err) => {
            console.error(err);
            Sentry.captureException(err);
        },
        update: (cache, { data: { created } }) => {
            cache.updateQuery(
                {
                    query: GET_INVOICE,
                    variables: { invoice_id },
                },
                (data) => {
                    return {
                        ...data,
                        result: {
                            ...data.result,
                            notes: [...(data.result.notes || []), ...(created.returning || [])],
                        },
                    };
                }
            );
        },
    });

    const [saveSupplementalChargesMutation, { loading: saveSupplementalInFlight }] = useMutation(
        SAVE_SUPPLEMENTAL_CHARGES,
        {
            onError: (err) => {
                console.error(err);
                Sentry.captureException(err);
            },
            update: (cache, { data: { created } }) => {
                cache.updateQuery(
                    {
                        query: GET_INVOICE,
                        variables: { invoice_id },
                    },
                    (data) => ({
                        ...data,
                        result: {
                            ...data.result,
                            supplemental_charges: created?.returning || [],
                        },
                    })
                );
            },
        }
    );

    // Local-first supplemental charges: null means "no pending edits, use server data"
    const [localSupplementalCharges, setLocalSupplementalCharges] = useState(null);
    let nextTempId = 0;

    const invoice = useMemo(() => {
        return data?.result;
    }, [data]);

    const editsBy = useMemo(() => {
        return editsDueDate(invoice?.pay_period_end);
    }, [invoice]);

    const orders = useMemo(() => invoiceLegRows(invoice), [invoice]);

    // Unique orders, since one payee can hold two legs of the same order and each leg is its own row.
    const orderful = useMemo(() => {
        const legOrders = orders.map(({ order }) => order);
        return {
            missing: countOrdersRemaining210(invoice, legOrders),
            total: new Set(legOrders.map((order) => order.order_id)).size,
        };
    }, [invoice, orders]);

    const supplementalCharges = useMemo(() => {
        return localSupplementalCharges ?? (invoice?.supplemental_charges || []);
    }, [localSupplementalCharges, invoice]);

    const hasSupplementalChanges = localSupplementalCharges !== null;

    const supplementalChargesTotal = useMemo(() => {
        return supplementalCharges.reduce(
            (acc, charge) => acc + (Number(charge.rate) || 0) * (Number(charge.quantity) || 0),
            0
        );
    }, [supplementalCharges]);

    const [subtotal, accessorials, claims, total] = useMemo(() => {
        if (!invoice || !orders.length) return [0, 0, 0, 0];

        const [subtotal, accessorials] = orders.reduce(
            (acc, { order, perspective }) => {
                const orderSubtotal = calcSubtotal(invoice, order, perspective);
                const orderAccessorials = calcAccessorials(invoice, order, perspective);
                return [acc[0] + orderSubtotal, acc[1] + orderAccessorials];
            },
            [0, 0]
        );
        const claims = calcClaims(invoice);
        const total = subtotal + accessorials - claims + supplementalChargesTotal;
        return [subtotal, accessorials, claims, total];
    }, [invoice, orders, supplementalChargesTotal]);

    const missingPOD = useMemo(() => !orders.every(({ order }) => hasPOD(order)), [orders]);

    const hasPODsAvailable = useMemo(() => orders.some(({ order }) => hasPOD(order)), [orders]);

    // Fire invoice_refreshed so subscribed accounting integrations (e.g. NetSuite) re-push this
    // invoice. The NetSuite upsert is idempotent, so this safely overwrites the existing record.
    const refreshIntegrations = async () => {
        setRefreshInFlight(true);
        try {
            return await post(TRIGGER_ORDER_EVENT, { invoice_ids: [invoice_id], event: 'invoice_refreshed' });
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        } finally {
            setRefreshInFlight(false);
        }
    };

    // Re-fire the approval event for one row so subscribed integrations (e.g. Orderful's 210) resend it.
    const reemitApproved = async (row) => {
        const event = invoiceApprovedEvent(invoice, row.perspective);
        if (!event) return;

        const rowId = legRowId(row);
        setReemitInFlight((prev) => ({ ...prev, [rowId]: true }));
        try {
            return await post(TRIGGER_ORDER_EVENT, { order_ids: [row.order.order_id], event });
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        } finally {
            setReemitInFlight((prev) => ({ ...prev, [rowId]: false }));
        }
    };

    const invoiceIdx = useMemo(() => {
        return allInvoices.findIndex((i) => i.carrier_invoice_id === invoice_id);
    }, [allInvoices, invoice_id]);

    const goToNextInvoice = useCallback(
        (direction) => {
            const newInvoice = allInvoices[invoiceIdx + direction];
            const path = ROUTES.ACCOUNTING_INVOICE.replace(':invoice_id', newInvoice.carrier_invoice_id);

            return navigate(path, {
                state: {
                    allInvoices: allInvoices || [],
                },
            });
        },
        [invoiceIdx, allInvoices]
    );

    const QUICKBOOKS_CSV_COLUMNS = useQuickbooksColumns({ invoice });
    const isElectroluxExportEnabled = !!invoice?.client?.user?.circles?.['electrolux-custom-export'];
    // Electrolux takes precedence when a client somehow carries both circles, matching the receivables
    // list and buildArtifacts on the NetSuite side.
    const masterbrandExportEnabled = !isElectroluxExportEnabled && isMasterbrandExportEnabled(invoice);
    const pelotonExportEnabled =
        !isElectroluxExportEnabled && !masterbrandExportEnabled && isPelotonExportEnabled(invoice);

    const downloadAllPODs = async () => {
        if (!hasPODsAvailable) return;

        setDownloadingPODs(true);
        try {
            const zip = new JSZip();
            const corruptFiles = [];
            let hasAnyFiles = false;

            // A two-leg order is two rows but one POD, and the zip is keyed by order number.
            for (const order of new Set(orders.map(({ order }) => order))) {
                if (order.pod) {
                    const pods = await getOrdersFilesAsPDF(order, 'pod', corruptFiles);
                    if (pods) {
                        hasAnyFiles = true;
                        zip.file(`${order.order_number} - POD.pdf`, await pods.save());
                    }
                }
            }

            if (hasAnyFiles) {
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                saveAs(zipBlob, `Invoice ${invoice.invoice_number} - PODs.zip`);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
        } finally {
            setDownloadingPODs(false);
        }
    };

    const errorMessage = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

    const openRevert = useCallback(async () => {
        setRevertError(null);
        const request = ++revertPreviewRequest.current;
        setRevertPreview({ loading: true });
        try {
            const { data } = await post(REVERT_INVOICES_PREVIEW, { invoice_id });
            if (request === revertPreviewRequest.current) setRevertPreview(data);
        } catch (err) {
            Sentry.captureException(err);
            if (request === revertPreviewRequest.current) {
                setRevertPreview({ error: errorMessage(err, 'Could not load the revert preview.') });
            }
        }
    }, [invoice_id]);

    // The endpoint answers with every invoice in the group; writing them into the cache updates this page in place.
    const [confirmRevert, { loading: revertInFlight }] = useAction(
        (reason) =>
            post(REVERT_INVOICES, {
                invoice_id,
                invoice_ids: revertPreview.invoices.map((inv) => inv.carrier_invoice_id),
                reason,
            }),
        {
            onComplete: ({ data }) => {
                (data.invoices || []).forEach((inv) =>
                    apolloClient.cache.writeFragment({
                        id: apolloClient.cache.identify(inv),
                        fragment: INVOICE_REVERT_FIELDS,
                        fragmentName: 'InvoiceRevertFields',
                        data: inv,
                    })
                );
                setRevertPreview(null);
                setRevertError(data.error || null);
                setRevertNotification(
                    data.error
                        ? { severity: 'error', message: data.error }
                        : { severity: 'success', message: `Reverted ${data.invoices.length} invoice(s).` }
                );
            },
            onError: (err) => {
                // The group changed since the preview: show the current one rather than let the user resubmit a stale one.
                if (err?.response?.status === 409) {
                    openRevert();
                    return;
                }
                Sentry.captureException(err);
                setRevertPreview((prev) => ({ ...prev, error: errorMessage(err, 'Revert failed.') }));
            },
        }
    );

    const callbacks = {
        exportCsv: () => {
            const exportData = generateLineItems([invoice]);
            csvDownload({
                headers: QUICKBOOKS_CSV_COLUMNS.map((col) => col.header),
                filename: `invoice-${invoice.invoice_number}-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    QUICKBOOKS_CSV_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        exportElectroluxCsv: () => {
            const exportData = generateElectroluxRows([invoice]);
            csvDownload({
                headers: ELECTROLUX_COLUMNS.map((col) => col.header),
                filename: `invoice-${invoice.invoice_number}-electrolux-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    ELECTROLUX_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        exportElectroluxTxt: () => {
            const exportData = generateElectroluxRows([invoice]);
            const txtContent = buildDelimitedText(exportData, ELECTROLUX_COLUMNS, {
                delimiter: '\t',
                lineEnding: '\n',
            });
            const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
            saveAs(blob, `invoice-${invoice.invoice_number}-electrolux-${format(new Date(), 'yyyy-MM-dd')}.txt`);
        },
        // One row per order, unlike the line-item-level default and Electrolux exports.
        exportMasterbrandCsv: () => {
            const exportData = generateMasterbrandRows([invoice]);
            csvDownload({
                headers: MASTERBRAND_COLUMNS.map((col) => col.header),
                filename: `invoice-${invoice.invoice_number}-masterbrand-${format(new Date(), 'yyyy-MM-dd')}.csv`,
                delimiter: ',',
                data: exportData.map((row) =>
                    MASTERBRAND_COLUMNS.map((col) => {
                        const value = col.getValue(row);
                        return col.format ? col.format(value) : value;
                    })
                ),
            });
        },
        exportPelotonCsv: () => downloadPelotonCsv([invoice], `invoice-${invoice.invoice_number}`),
        setSubmitted: () => {
            updateInvoice({
                variables: {
                    invoice_id: invoice.carrier_invoice_id,
                    patch: {
                        status: 'UNPAID',
                    },
                },
            });
        },
        setPaid: () => {
            updateInvoice({
                variables: {
                    invoice_id: invoice.carrier_invoice_id,
                    patch: {
                        status: 'PAID',
                    },
                },
            });
        },
        setApproved: () => {
            updateInvoice({
                variables: {
                    invoice_id: invoice.carrier_invoice_id,
                    patch: {
                        status: 'APPROVED',
                    },
                },
            });
        },
        viewOrderPod,
        setNewComment,
        saveComments: () => {
            insertNotes({
                variables: {
                    notes: [
                        {
                            invoice_id: invoice.carrier_invoice_id,
                            note: newComment,
                            source_user_id: user.user_id,
                        },
                    ],
                },
            }).then(() => {
                setNewComment('');
            });
        },
        gotoAccessorial: (order, perspective) => {
            navigate(`/accounting/accessorials/${order.order_id}/${perspective}`, { target: '_blank' });
        },
        gotoDetails: (order) => {
            navigate(`/order/${order.order_id}`, { target: '_blank' });
        },
        getRowId: legRowId,
        refreshIntegrations,
        reemitApproved,
        goToNextInvoice,
        downloadAllPODs,
        addSupplementalCharge: (charge) => {
            setLocalSupplementalCharges((prev) => {
                const current = prev ?? (invoice?.supplemental_charges || []);
                return [...current, { ...charge, _tempId: `temp_${++nextTempId}_${Date.now()}` }];
            });
        },
        editSupplementalCharge: (idOrTempId, patch) => {
            setLocalSupplementalCharges((prev) => {
                const current = prev ?? (invoice?.supplemental_charges || []);
                return current.map((c) => {
                    const match = c.supplemental_charge_id === idOrTempId || c._tempId === idOrTempId;
                    return match ? { ...c, ...patch } : c;
                });
            });
        },
        removeSupplementalCharge: (idOrTempId) => {
            setLocalSupplementalCharges((prev) => {
                const current = prev ?? (invoice?.supplemental_charges || []);
                return current.filter((c) => c.supplemental_charge_id !== idOrTempId && c._tempId !== idOrTempId);
            });
        },
        discardSupplementalChanges: () => {
            setLocalSupplementalCharges(null);
        },
    };

    const isAdmin = useMemo(() => {
        const isInternalAdmin = (user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN']) && !client?.client_id;
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] &&
            client?.user?.circles?.[ASSET_HEAVY] &&
            (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]));
        return isInternalAdmin || isCarrierAdmin;
    }, [user, client, teammate]);

    const canViewIntegrations = useCanViewIntegrations();

    const breadcrumbs = useMemo(() => {
        if (!invoice) return [];

        let crumbs = [];

        if (isAdmin) {
            switch (invoice.type) {
                case 'RECEIVABLE':
                case 'ONWARD_RECEIVABLE':
                    crumbs.push({
                        active: false,
                        label: 'Receivables',
                        route: ROUTES.ACCOUNTING_RECEIVABLES,
                    });
                    break;
                case 'PAYABLE':
                case 'ONWARD_PAYABLE':
                    crumbs.push({
                        active: false,
                        label: 'Payables',
                        route: ROUTES.ACCOUNTING_PAYABLES,
                    });
                    break;
            }
        } else {
            crumbs.push({
                active: false,
                label: 'Invoices',
                route:
                    invoice.type === 'INTERNAL'
                        ? ROUTES.CARRIER_INTERNAL_INVOICES
                        : ROUTES.CARRIER_MARKETPLACE_INVOICES,
            });
        }

        crumbs.push({
            active: true,
            label: invoice.invoice_number,
        });

        return crumbs;
    }, [isAdmin, invoice]);

    const COLUMNS = useTableColumns({ invoice, callbacks, canViewIntegrations, reemitInFlight });

    return (
        <Context.Provider
            value={{
                state: {
                    editsBy,
                    viewPod,
                    total,
                    subtotal,
                    missingPOD,
                    hasPODsAvailable,
                    accessorials,
                    claims,
                    supplementalCharges,
                    supplementalChargesTotal,
                    invoice,
                    invoiceTypeLabel: invoice?.type
                        ? INVOICE_TYPE_DISPLAY_MAPPINGS[invoice.type] || invoice.type
                        : null,
                    orders,
                    newComment,
                    columns: COLUMNS,
                    isAdmin,
                    canViewIntegrations,
                    orderfulMissing: orderful.missing,
                    orderfulTotal: orderful.total,
                    invoiceIdx,
                    allInvoices,
                    breadcrumbs,
                    isElectroluxExportEnabled,
                    masterbrandExportEnabled,
                    pelotonExportEnabled,
                    revertNotification,
                    revertError,
                    canRevert: isAdmin && !!invoice && invoice.status !== 'REVERTED',
                    revertIncomplete:
                        invoice?.status !== 'REVERTED' &&
                        (invoice?.accounting_records || []).some((r) =>
                            ['REVERTED', 'REVERT_FAILED'].includes(r.status)
                        ),
                    revertPreview,
                    canEditSupplemental:
                        isAdmin &&
                        ['DRAFT', 'UNPAID'].includes(invoice?.status) &&
                        !invoice?.billcom_bill_id &&
                        !invoice?.netsuite_invoice_id,
                    hasSupplementalChanges,
                },
                loading: {
                    init: initInFlight,
                    update: updateInFlight,
                    notes: notesInFlight,
                    downloadingPODs,
                    revert: revertInFlight,
                    supplemental: saveSupplementalInFlight,
                    refreshing: refreshInFlight,
                },
                callbacks: {
                    ...callbacks,
                    openRevert,
                    closeRevert: () => {
                        revertPreviewRequest.current += 1;
                        setRevertPreview(null);
                    },
                    confirmRevert,
                    dismissRevertNotification: () => setRevertNotification(null),
                    saveSupplementalCharges: () => {
                        if (!localSupplementalCharges) return;
                        const charges = localSupplementalCharges.map(
                            ({ _tempId, supplemental_charge_id, created_at, __typename, ...rest }) => ({
                                ...rest,
                                invoice_id: invoice.carrier_invoice_id,
                            })
                        );
                        saveSupplementalChargesMutation({
                            variables: {
                                invoice_id: invoice.carrier_invoice_id,
                                charges,
                            },
                        }).then(() => {
                            setLocalSupplementalCharges(null);
                        });
                    },
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
